import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Solid pills per the statusColors.js convention: bg = the color, text =
// #1a1a1a (lighter) or #ffffff (deeper). Approved/Rejected use the app's
// semantic tokens (Doorly green / brand red) so they read as success/failure;
// the remaining statuses use the secondary brand palette.
const STATUS_COLORS = {
  pending_review: { bg: '#FF7A1A', color: '#1a1a1a', label: 'Pending Review' }, // Orange (secondary)
  approved:       { bg: '#acf44e', color: '#1a1a1a', label: 'Approved' },        // Doorly green (semantic)
  rejected:       { bg: '#ff082f', color: '#ffffff', label: 'Rejected' },        // Brand red (semantic)
  sent:           { bg: '#0EA5E9', color: '#1a1a1a', label: 'Sent' },            // Cyan (secondary)
  replied:        { bg: '#7C58F5', color: '#ffffff', label: 'Replied' },         // Violet (secondary)
  published:      { bg: '#D946C7', color: '#ffffff', label: 'Published' },        // Magenta (secondary)
};

// Angle tags rotate through the 8 secondary brand LIGHT variants (soft pills).
const ANGLE_COLORS = [
  '#FFEBDC', // orange-light
  '#FFE3EE', // coral-light
  '#FCE2F8', // magenta-light
  '#ECE5FE', // violet-light
  '#E3E2FB', // indigo-light
  '#D5F4F0', // teal-light
  '#DCF1FC', // cyan-light
  '#E7EAF0', // slate-light
];

// 5 pitch types — kept in sync with the switch in
// backend/routes/agents.js → POST /pr/generate-pitch.
const PITCH_TYPES = [
  { value: 'news_hook',      label: 'News Hook',         desc: "Connect breaking news to Doorly's story",
    placeholder: 'e.g. "Fed held rates again — impact on non-traditional buyers?"' },
  { value: 'reporter_query', label: 'Reporter Query',    desc: "Respond to a journalist's query",
    placeholder: "Paste the reporter's query here..." },
  { value: 'guest_post',     label: 'Guest Post',        desc: "Pitch Jesse writing for their publication",
    placeholder: 'e.g. "Why self-employed buyers keep getting denied and what to do about it"' },
  { value: 'backlink',       label: 'Backlink Outreach', desc: 'Request a link from a high-authority site',
    placeholder: 'e.g. "NerdWallet mortgage guide page" or "first-time buyer resource sites"' },
  { value: 'press_release',  label: 'Press Release',     desc: 'Announce a company milestone',
    placeholder: 'e.g. "Doorly iOS app now live on the App Store"' },
];

// Badges on PitchCard headers — soft pills tied to the pitch type, distinct
// palette from STATUS_COLORS so type vs status read cleanly side-by-side.
const PITCH_TYPE_LABELS = {
  news_hook:      { label: 'News Hook',     bg: '#eff6ff', color: '#1e40af' },
  reporter_query: { label: 'Query Reply',   bg: '#f3e8ff', color: '#6b21a8' },
  guest_post:     { label: 'Guest Post',    bg: '#fef9c3', color: '#854d0e' },
  backlink:       { label: 'Backlink',      bg: '#dcfce7', color: '#166534' },
  press_release:  { label: 'Press Release', bg: '#fce7f3', color: '#9d174d' },
};

const PR_STATUS_COLORS = {
  draft:          { bg: '#f3f4f6', color: '#374151' },
  pending_review: { bg: '#fef9c3', color: '#854d0e' },
  approved:       { bg: '#eff6ff', color: '#1e40af' },
  distributed:    { bg: '#dcfce7', color: '#166534' },
  published:      { bg: '#f0fdf4', color: '#166534' },
};

export default function AdminPrOutreach() {
  const [pitches, setPitches]       = useState([]);
  const [targets, setTargets]       = useState([]);
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('pitches');
  const [processing, setProcessing] = useState({});

  // Generate pitch form
  const [hookType, setHookType]     = useState('news_hook');
  const [hookText, setHookText]     = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError]     = useState('');

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [pRes, tRes, prRes] = await Promise.all([
        axios.get(`${API}/api/admin/agents/pr/pitches?status=all`, { headers }),
        axios.get(`${API}/api/admin/agents/pr/targets`, { headers }),
        axios.get(`${API}/api/admin/agents/pr/press-releases`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPitches(pRes.data || []);
      setTargets(tRes.data || []);
      setPressReleases(prRes.data || []);
    } catch (err) {
      console.error('Failed to load PR data:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePitch = async (id, action, reason) => {
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await axios.patch(
        `${API}/api/admin/agents/pr/pitches/${id}/${action}`,
        action === 'reject' ? { reason } : {},
        { headers }
      );
      await fetchData();
    } catch (err) {
      console.error(err.message);
    } finally {
      setProcessing(p => ({ ...p, [id]: false }));
    }
  };

  const handleGenerate = async () => {
    if (!hookText.trim()) return;
    setGenerating(true);
    setGenError('');
    try {
      await axios.post(
        `${API}/api/admin/agents/pr/generate-pitch`,
        { hook: hookText, type: hookType },
        { headers }
      );
      setHookText('');
      setActiveTab('pitches');
      await fetchData();
    } catch (err) {
      setGenError(err.response?.data?.error || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const pending  = pitches.filter(p => p.status === 'pending_review');
  const sent     = pitches.filter(p => ['sent','replied','published'].includes(p.status));
  const approved = pitches.filter(p => p.status === 'approved');

  const tabStyle = (tab) => ({
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: activeTab === tab ? '#3a6ad9' : 'transparent',
    color: activeTab === tab ? '#fff' : '#6b7280',
  });

  const PitchCard = ({ pitch, showActions }) => {
    const sc = STATUS_COLORS[pitch.status] || STATUS_COLORS.pending_review;
    const [expanded, setExpanded] = useState(false);

    return (
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: '18px 22px',
        marginBottom: 12,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 14, fontWeight: 700,
              color: '#111827', marginBottom: 3,
            }}>
              {pitch.subject}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {pitch.journalist_name
                ? `${pitch.journalist_name} · ${pitch.publication}`
                : pitch.publication || 'No journalist assigned'}
              {pitch.angle && (
                <span style={{
                  marginLeft: 8,
                  background: ANGLE_COLORS[pitch.id % ANGLE_COLORS.length],
                  color: '#374151',
                  borderRadius: 6,
                  padding: '1px 8px',
                  fontSize: 11,
                  fontWeight: 500,
                }}>
                  {pitch.angle}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 12, whiteSpace: 'nowrap' }}>
            {pitch.pitch_type && PITCH_TYPE_LABELS[pitch.pitch_type] && (
              <span style={{
                fontSize: 10, fontWeight: 600,
                background: PITCH_TYPE_LABELS[pitch.pitch_type].bg,
                color: PITCH_TYPE_LABELS[pitch.pitch_type].color,
                borderRadius: 6, padding: '2px 8px',
              }}>
                {PITCH_TYPE_LABELS[pitch.pitch_type].label}
              </span>
            )}
            <span style={{
              fontSize: 11, fontWeight: 600,
              background: sc.bg, color: sc.color,
              borderRadius: 999, padding: '3px 10px',
            }}>
              {sc.label}
            </span>
          </div>
        </div>

        {/* Body preview */}
        <div style={{
          background: '#f9fafb',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12,
          color: '#374151',
          lineHeight: 1.6,
          marginBottom: showActions ? 12 : 0,
          whiteSpace: 'pre-wrap',
          maxHeight: expanded ? 'none' : 120,
          overflow: 'hidden',
          position: 'relative',
        }}>
          {pitch.body}
          {!expanded && pitch.body?.length > 300 && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 40,
              background: 'linear-gradient(transparent, #f9fafb)',
            }} />
          )}
        </div>

        {pitch.body?.length > 300 && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'none', border: 'none',
              color: '#3a6ad9', fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
              padding: '4px 0', marginBottom: showActions ? 8 : 0,
            }}>
            {expanded ? 'Show less ↑' : 'Read full pitch ↓'}
          </button>
        )}

        {/* Outreach tracking for sent pitches */}
        {['sent','replied','published'].includes(pitch.status) && (
          <div style={{
            display: 'flex', gap: 12,
            marginBottom: showActions ? 10 : 0,
            flexWrap: 'wrap',
          }}>
            {[
              { label: 'Sent', value: pitch.sent_at },
              { label: 'Opened', value: pitch.opened_at },
              { label: 'Replied', value: pitch.replied_at },
            ].map(t => (
              <div key={t.label} style={{
                fontSize: 11,
                color: t.value ? '#14B8A6' : '#9ca3af', // Teal (brand) for done, gray for not
                fontWeight: 500,
              }}>
                {t.value ? '✓' : '○'} {t.label}
                {t.value && (
                  <span style={{ marginLeft: 4, fontWeight: 400 }}>
                    {new Date(t.value).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric',
                    })}
                  </span>
                )}
              </div>
            ))}
            {pitch.published_url && (
              <a href={pitch.published_url} target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, color: '#3a6ad9', fontWeight: 600 }}>
                View coverage →
              </a>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && pitch.status === 'pending_review' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={processing[pitch.id]}
              onClick={() => handlePitch(pitch.id, 'approve')}
              style={{
                background: processing[pitch.id] ? '#9ca3af' : '#3a6ad9',
                color: '#fff', border: 'none', borderRadius: 7,
                padding: '7px 16px', fontSize: 12, fontWeight: 600,
                cursor: processing[pitch.id] ? 'default' : 'pointer',
              }}>
              Approve
            </button>
            <button
              disabled={processing[pitch.id]}
              onClick={() => {
                const reason = window.prompt('Reason for rejection (optional):');
                if (reason !== null) handlePitch(pitch.id, 'reject', reason);
              }}
              style={{
                background: '#fff', color: '#374151',
                border: '1px solid #e5e7eb', borderRadius: 7,
                padding: '7px 16px', fontSize: 12, fontWeight: 600,
                cursor: processing[pitch.id] ? 'default' : 'pointer',
              }}>
              Reject
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>
            Marketing
          </div>
          <h1 style={{
            fontSize: 22, fontWeight: 700,
            color: '#111827', margin: 0,
          }}>
            PR Outreach
          </h1>
        </div>

        {/* Generate Pitch panel */}
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '20px 22px',
          marginBottom: 24,
        }}>
          <div style={{
            fontSize: 14, fontWeight: 700,
            color: '#111827', marginBottom: 12,
          }}>
            Generate Pitch
          </div>

          {/* Type selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
            {PITCH_TYPES.map(pt => (
              <label key={pt.value} style={{
                display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer',
                padding: '10px 12px',
                border: `1px solid ${hookType === pt.value ? '#3a6ad9' : '#e5e7eb'}`,
                borderRadius: 8,
                background: hookType === pt.value ? '#eff6ff' : '#fff',
              }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="radio" value={pt.value}
                    checked={hookType === pt.value}
                    onChange={() => setHookType(pt.value)} />
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{pt.label}</div>
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.4 }}>{pt.desc}</div>
              </label>
            ))}
          </div>

          <textarea
            value={hookText}
            onChange={e => setHookText(e.target.value)}
            placeholder={PITCH_TYPES.find(pt => pt.value === hookType)?.placeholder || 'Describe what you need...'}
            style={{
              width: '100%',
              minHeight: 90,
              padding: '10px 14px',
              fontSize: 13,
              color: '#111827',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              marginBottom: 10,
            }}
          />

          {genError && (
            <div style={{
              background: '#fff0f3',        // brand error-light
              border: '1px solid #ff082f',  // brand error
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 12,
              color: '#ff082f',
              marginBottom: 10,
            }}>
              {genError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              disabled={generating || !hookText.trim()}
              onClick={handleGenerate}
              style={{
                background: generating || !hookText.trim()
                  ? '#9ca3af' : '#3a6ad9',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: generating || !hookText.trim()
                  ? 'default' : 'pointer',
              }}>
              {generating ? 'Generating...' : 'Generate Pitch Ideas →'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4,
          background: '#f3f4f6',
          borderRadius: 10, padding: 4,
          width: 'fit-content',
          marginBottom: 20,
        }}>
          <button style={tabStyle('pitches')}
            onClick={() => setActiveTab('pitches')}>
            Pending Review
            {pending.length > 0 && (
              <span style={{
                marginLeft: 6, background: '#ff082f',
                color: '#fff', borderRadius: 999,
                fontSize: 10, fontWeight: 700,
                padding: '1px 6px',
              }}>
                {pending.length}
              </span>
            )}
          </button>
          <button style={tabStyle('outreach')}
            onClick={() => setActiveTab('outreach')}>
            In Flight ({sent.length})
          </button>
          <button style={tabStyle('approved')}
            onClick={() => setActiveTab('approved')}>
            Approved ({approved.length})
          </button>
          <button style={tabStyle('targets')}
            onClick={() => setActiveTab('targets')}>
            Journalists ({targets.length})
          </button>
          <button style={tabStyle('press_releases')}
            onClick={() => setActiveTab('press_releases')}>
            Press Releases ({pressReleases.length})
          </button>
        </div>

        {loading && (
          <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>
            Loading...
          </div>
        )}

        {/* Pending Review tab */}
        {!loading && activeTab === 'pitches' && (
          <>
            {pending.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10, padding: '32px',
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No pitches pending review — use Generate Pitch above to create new ones
              </div>
            )}
            {pending.map(p => (
              <PitchCard key={p.id} pitch={p} showActions={true} />
            ))}
          </>
        )}

        {/* In Flight tab */}
        {!loading && activeTab === 'outreach' && (
          <>
            {sent.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10, padding: '32px',
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No pitches in flight yet
              </div>
            )}
            {sent.map(p => (
              <PitchCard key={p.id} pitch={p} showActions={false} />
            ))}
          </>
        )}

        {/* Approved tab */}
        {!loading && activeTab === 'approved' && (
          <>
            {approved.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10, padding: '32px',
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No approved pitches yet
              </div>
            )}
            {approved.map(p => (
              <PitchCard key={p.id} pitch={p} showActions={false} />
            ))}
          </>
        )}

        {/* Journalists tab */}
        {!loading && activeTab === 'targets' && (
          <div>
            {targets.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10, padding: '32px',
                textAlign: 'center', color: '#6b7280', fontSize: 13,
              }}>
                No journalists in database yet
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 12,
            }}>
              {targets.map(t => (
                <div key={t.id} style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '16px 18px',
                }}>
                  <div style={{
                    fontSize: 14, fontWeight: 700,
                    color: '#111827', marginBottom: 2,
                  }}>
                    {t.journalist_name}
                  </div>
                  <div style={{
                    fontSize: 12, color: '#3a6ad9',
                    fontWeight: 600, marginBottom: 4,
                  }}>
                    {t.publication}
                  </div>
                  {t.beat && (
                    <div style={{
                      fontSize: 11, color: '#6b7280', marginBottom: 8,
                    }}>
                      {t.beat}
                    </div>
                  )}
                  <div style={{
                    display: 'flex', gap: 8, flexWrap: 'wrap',
                  }}>
                    {t.email && (
                      <a href={`mailto:${t.email}`} style={{
                        fontSize: 11, color: '#374151',
                        background: '#f3f4f6',
                        borderRadius: 6, padding: '2px 8px',
                        textDecoration: 'none',
                      }}>
                        {t.email}
                      </a>
                    )}
                    {t.twitter_handle && (
                      <a href={`https://twitter.com/${t.twitter_handle.replace('@','')}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{
                          fontSize: 11, color: '#374151',
                          background: '#f3f4f6',
                          borderRadius: 6, padding: '2px 8px',
                          textDecoration: 'none',
                        }}>
                        {t.twitter_handle}
                      </a>
                    )}
                    {t.linkedin_url && (
                      <a href={t.linkedin_url} target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 11, color: '#374151',
                          background: '#f3f4f6',
                          borderRadius: 6, padding: '2px 8px',
                          textDecoration: 'none',
                        }}>
                        LinkedIn
                      </a>
                    )}
                  </div>
                  {t.notes && (
                    <div style={{
                      fontSize: 11, color: '#9ca3af',
                      marginTop: 8, lineHeight: 1.4,
                    }}>
                      {t.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Press Releases tab */}
        {!loading && activeTab === 'press_releases' && (
          <div>
            {pressReleases.length === 0 && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                No press releases yet — generate one with the Press Release pitch type above, then approve it
              </div>
            )}
            {pressReleases.map(pr => {
              const sc = PR_STATUS_COLORS[pr.status] || PR_STATUS_COLORS.draft;
              return (
                <div key={pr.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 22px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 3 }}>{pr.title}</div>
                      {pr.summary && <div style={{ fontSize: 12, color: '#6b7280' }}>{pr.summary}</div>}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap', marginLeft: 12 }}>
                      {pr.status.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
                    </span>
                  </div>

                  {/* Body preview — fades out so admins click through to copy */}
                  <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'hidden', position: 'relative', marginBottom: 12, fontFamily: 'Georgia, serif' }}>
                    {pr.body}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, background: 'linear-gradient(transparent, #f9fafb)' }} />
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {pr.status === 'pending_review' && (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/api/admin/agents/pr/press-releases/${pr.id}/approve`, {}, { headers });
                              await fetchData();
                            } catch (err) { alert(err?.response?.data?.error || err.message || 'Approve failed.'); }
                          }}
                          style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const reason = window.prompt('Reason for rejection (optional):');
                            if (reason === null) return;
                            try {
                              await axios.patch(`${API}/api/admin/agents/pr/press-releases/${pr.id}/reject`, { reason }, { headers });
                              await fetchData();
                            } catch (err) { alert(err?.response?.data?.error || err.message || 'Reject failed.'); }
                          }}
                          style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Reject
                        </button>
                      </>
                    )}
                    {pr.status === 'approved' && (
                      <button
                        onClick={async () => {
                          const service = window.prompt(
                            'Distribution service:\npr_newswire / business_wire / ein_presswire / manual',
                            'manual'
                          );
                          if (service === null) return;
                          try {
                            await axios.patch(`${API}/api/admin/agents/pr/press-releases/${pr.id}/mark-distributed`, { service }, { headers });
                            await fetchData();
                          } catch (err) { alert(err?.response?.data?.error || err.message || 'Mark distributed failed.'); }
                        }}
                        style={{ background: '#acf44e', color: '#111827', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Mark Distributed →
                      </button>
                    )}
                    {pr.status === 'distributed' && (
                      <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, padding: '7px 0' }}>
                        ✓ Distributed via {pr.distribution_service}
                        {pr.distributed_at && (
                          <span style={{ fontWeight: 400, color: '#6b7280' }}>
                            {' '}· {new Date(pr.distributed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(pr.body);
                        alert('Press release copied to clipboard');
                      }}
                      style={{ background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                      Copy Text
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
